import { DOCUMENT, DestroyRef, Injectable, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { OptionGroupType } from '@frontend/sports/betting-offer/feature/fixture-factories';
import { EventModel } from '@frontend/sports/betting-offer/feature/model';
import { Store } from '@ngrx/store';
import { Subscription, filter, fromEvent } from 'rxjs';

import { loadPlayerStatisticsData } from './player-statistics.actions';
import { PlayerStatisticsStoreData } from './player-statistics.model';
import { PlayerStatisticsService } from './player-statistics.service';
import { selectDataByEventAndMarket } from './player-statistics.state';

@Injectable()
export class BaseOptionGroupStatisticsService {
    protected readonly store = inject(Store);
    protected readonly destroyRef = inject(DestroyRef);
    protected readonly playerStatisticsService = inject(PlayerStatisticsService);
    private playerStatisticsSubscription: Subscription;
    private readonly _document = inject(DOCUMENT);
    private listenerInitialized = false;
    // Player statistics properties
    readonly isPlayerStatisticsEnabled = signal(false);
    readonly isSeasonStatisticsEnabled = signal(false);
    readonly playerStatistics = signal<PlayerStatisticsStoreData>({
        isLoading: true,
        isError: false,
        data: undefined,
    });
    readonly iFrameHeight = signal<{ [key: string]: string }>({});

    initializePlayerStatistics(event: EventModel, optionGroupType: OptionGroupType): void {
        this.isPlayerStatisticsEnabled.set(this.playerStatisticsService.isPlayerStatisticsVisible(event.league.id, optionGroupType));
        this.isSeasonStatisticsEnabled.set(this.playerStatisticsService.isSeasonStatisticsVisible(event.league.id, optionGroupType));
    }

    initIframeHeightListener(isMainMarket: boolean): void {
        if (this.listenerInitialized) {
            return;
        }
        this.listenerInitialized = true;

        const window = this._document.defaultView;
        if (!window) {
            return;
        }

        fromEvent<MessageEvent>(window, 'message')
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                filter((event) => {
                    const allowedOrigins = /^https:\/\/([a-z0-9-]+\.)*isportgenius\.com\.au$/;
                    return allowedOrigins.test(event.origin) && event.data && event.data.type === 'height-adjustment' && event.data.height;
                }),
            )
            .subscribe((event) => {
                const msg = event.data;
                const key = isMainMarket && !msg.outcomeID ? msg.eventID : msg.outcomeID;
                if (key) {
                    this.iFrameHeight.update((prev) => ({
                        ...prev,
                        [key]: `${msg.height}px`,
                    }));
                }
            });
    }

    fetchPlayerStatistics(event: EventModel, optionGroup: any, mainMarket = false): void {
        if (this.isPlayerStatisticsEnabled() || this.isSeasonStatisticsEnabled()) {
            const eventId = event.id;
            const marketFilter = optionGroup.originalId;
            if (mainMarket) {
                this.store.dispatch(loadPlayerStatisticsData({ eventId, mainMarket: true }));
            } else {
                this.store.dispatch(loadPlayerStatisticsData({ eventId, marketFilter }));
            }

            this.subscribeToPlayerStatisticsState(eventId, marketFilter, mainMarket);
            this.initIframeHeightListener(mainMarket);
        }
    }

    private subscribeToPlayerStatisticsState(eventId: string, marketFilter: string, mainMarket = false): void {
        this.playerStatisticsSubscription?.unsubscribe();
        this.playerStatisticsSubscription = this.store
            .select(selectDataByEventAndMarket(eventId, marketFilter, mainMarket))
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((data: PlayerStatisticsStoreData) => {
                if (data) {
                    this.playerStatistics.set({
                        isLoading: data.isLoading,
                        isError: data.isError,
                        data: data.data,
                    });
                }
            });
    }
}
